import { Button, FormStatus, StepIndicator } from '@dnb/eufemia';
import { Form } from '@dnb/eufemia/extensions/forms';
import { type JSX, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { createMcp } from '@/api/api';
import LoadingModal from '@/components/LoadingModal';
import ModalPage from '@/components/ModalPage';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { NotFoundPage } from '@/pages/NotFoundPage';

import McpDetailsStep from './McpDetailsStep';
import type { AddMcpFormData } from './schema';
import SelectAppStep from './SelectAppStep';
import { McpStep, mcpSteps, mcpStepTitleMap } from './steps';
import SuccessModal from './SuccessModal';

const FORM_ID = 'add-mcp';

const DEFAULT_DATA: AddMcpFormData = {
  teamId: undefined,
  appId: undefined,
  name: '',
  slug: '',
  description: '',
  classification: 'TECHNICAL',
  stage: 'LAUNCHED',
  guide: '',
};

export default function AddMcp(): JSX.Element {
  const navigate = useNavigate();
  const { featureFlags } = useFeatureFlags();
  const [step, setStep] = useState(0);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<{
    teamId: string;
    appId: string;
    mcpId: string;
    mcpSlug: string;
  } | null>(null);

  const { data } = Form.useData<AddMcpFormData>(FORM_ID, DEFAULT_DATA);
  const canContinue = Boolean(data?.teamId && data?.appId);
  const isLastStep = step === mcpSteps.length - 1;

  // Eufemia keeps form data in shared state keyed by FORM_ID, which survives
  // unmount. Reset on open so input from a previous visit is cleared. setData
  // merges, so DEFAULT_DATA must list every key (incl. teamId/appId) to reset.
  useEffect(() => {
    Form.setData(FORM_ID, DEFAULT_DATA);
  }, []);

  const handleSubmit = async (formData: AddMcpFormData) => {
    const { teamId, appId } = formData;
    if (!teamId || !appId) {
      setStep(0);
      return;
    }
    if (!formData.guide?.trim()) {
      setSubmitError('Add a guide before creating the MCP.');
      return;
    }
    try {
      setSubmitError(null);
      setSubmitting(true);
      const mcp = await createMcp(appId, formData);
      setCreated({ teamId, appId, mcpId: mcp.id, mcpSlug: mcp.slug });
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Something went wrong, please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!featureFlags.ENABLE_MCP_PAGES) {
    return <NotFoundPage />;
  }

  if (created) {
    return (
      <SuccessModal appId={created.appId} mcpId={created.mcpId} mcpSlug={created.mcpSlug} teamId={created.teamId} />
    );
  }

  return (
    <ModalPage
      onClose={() => navigate('/explorer/mcps')}
      subtitle="Publish your MCP to the developer portal"
      title="Add MCP"
    >
      {submitting && <LoadingModal />}

      <StepIndicator
        bottom="medium"
        currentStep={step}
        data={mcpSteps.map((s) => mcpStepTitleMap[s])}
        mode="loose"
        onChange={({ currentStep }) => {
          if (currentStep === 0 || canContinue) setStep(currentStep);
        }}
      />

      <Form.Handler defaultData={DEFAULT_DATA} id={FORM_ID} onSubmit={handleSubmit}>
        {mcpSteps[step] === McpStep.SelectApp && <SelectAppStep formId={FORM_ID} />}
        {mcpSteps[step] === McpStep.Details && <McpDetailsStep formId={FORM_ID} />}

        {submitError && <FormStatus state="error" stretch text={submitError} top="medium" />}

        <Form.ButtonRow top="medium">
          {step > 0 ? (
            <Button onClick={() => setStep((s) => s - 1)} size="large" variant="secondary">
              Back
            </Button>
          ) : (
            <Button onClick={() => navigate('/explorer/mcps')} size="large" variant="secondary">
              Cancel
            </Button>
          )}

          {isLastStep ? (
            <Form.SubmitButton disabled={submitting} size="large" text="Create MCP" />
          ) : (
            <Button
              disabled={!canContinue}
              icon="chevron_right"
              iconPosition="right"
              onClick={() => setStep((s) => s + 1)}
              size="large"
            >
              Next
            </Button>
          )}
        </Form.ButtonRow>
      </Form.Handler>
    </ModalPage>
  );
}
